import { Injectable } from '@angular/core';
import { ActivationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import { filter, map, shareReplay, startWith } from 'rxjs/operators';
import { getLocation } from '../../account-group/account-group';
import { LocationsService } from '../../locations';
import { PageId } from '../../page-access';
import { ReputationService } from '../../reputation';
import { FilterService } from '../../shared/filter-service.service';
import { AvailableTimeRangeOptions, TimeRangeService } from '../../shared/time-range.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export const COUNTRIES_LABEL = 'PERFORMANCE.MULTI_LOCATION.FILTERS.GEOGRAPHY.COUNTRIES';
export const STATEPROV_LABEL = 'PERFORMANCE.MULTI_LOCATION.FILTERS.GEOGRAPHY.REGION';
export const CITIES_LABEL = 'PERFORMANCE.MULTI_LOCATION.FILTERS.GEOGRAPHY.CITIES';

export interface DisplayFilter {
  filterId: string;
  filterTypeTranslateKey?: string;
  displayName: string;
  displayName$?: Observable<string>;
  icon?: string;
}

export interface FilterDisplaySettings {
  textColor: string;
  backgroundColor: string;
}

export interface LocationGroup {
  name: string;
  options: Location[];
}
export interface Location {
  id: string;
  name: string;
  parent: string[];
  locationCount: number;
}

export type DialogOpenTypes = 'location';

@Injectable({ providedIn: 'root' })
export class BrandFilterContainerService {
  private readonly dateFilterUsed$$ = new BehaviorSubject<boolean>(false);
  public readonly dateFilterUsed$ = this.dateFilterUsed$$.asObservable();
  public readonly dateFilterDisplay$: Observable<string>;

  private readonly selectedAccountGroupIds$$ = new BehaviorSubject<string[]>([]);
  public readonly selectedAccountGroupIds$ = this.selectedAccountGroupIds$$.asObservable();
  public readonly locationFilterDisplay$: Observable<string>;

  private readonly selectedSourceIds$$ = new BehaviorSubject<string[]>([]);
  public readonly selectedSourceIds$ = this.selectedSourceIds$$.asObservable();
  // TODO: Number if what I want most of the time but I've reached for a string (5+) when I didn't know exactly how many
  // TODO: ie, when filter for listings with name error OR website error -- overlapping counts with OR
  // TODO: Perhaps this isn't worth trying to put up here, let it be used in the locations that visualize better?
  private readonly sourceIdCounts$$ = new BehaviorSubject<Map<string, number>>(null);
  public readonly sourceIdCounts$ = this.sourceIdCounts$$.asObservable();
  public readonly sourceFilterDisplay$: Observable<string>;
  public readonly sourceOptions$: Observable<any[]>;

  openDialog$: Subject<DialogOpenTypes> = new Subject();
  // TODO: This is set in several places but never read. Missed in feature removal?
  private minimised$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public minimised$: Observable<boolean> = this.minimised$$.asObservable();

  shouldHaveShadow$: Observable<boolean>;

  constructor(
    private readonly timeRangeService: TimeRangeService,
    private readonly translate: TranslateService,
    private readonly filterService: FilterService,
    private readonly router: Router,
    private readonly reputationService: ReputationService,
    private readonly locationsService: LocationsService,
  ) {
    // Watch the selection to produce the english version of the selection
    this.dateFilterDisplay$ = this.timeRangeService.selectedTimeRangeKey$.pipe(
      map((timeRangeKey) => {
        const timeRangeSelected = AvailableTimeRangeOptions.find(
          (timeRangeOption) => timeRangeOption.key === timeRangeKey,
        );
        return `${timeRangeSelected.name}`;
      }),
    );

    this.locationFilterDisplay$ = combineLatest([
      // Loaded, as we need to know if the selection is in the next load to carry it over
      this.locationsService.currentAccountGroups$.pipe(filter((ls) => !!ls)),
      this.selectedAccountGroupIds$,
      this.translate.stream('COMMON.LOCATIONS'),
    ]).pipe(
      map(([brandLocations, accountGroupIds, locationsTranslated]) => {
        const locs = accountGroupIds.map((accountGroupId) => brandLocations[accountGroupId]);
        const locsInside = locs.filter((loc) => loc);
        // If not all are present, set to just present
        if (locs.length - locsInside.length) {
          this.setAccountGroupIds(locsInside.map((loc) => loc.accountGroupId));
          return '';
        }

        if (locsInside.length > 1) {
          return `${locsInside.length} ${locationsTranslated}`;
        } else {
          return locsInside.map((loc) => `${loc.companyName} ${getLocation(loc)}`).join(', ');
        }
      }),
    );

    this.sourceFilterDisplay$ = combineLatest([
      // Loaded, as we need to know if the selection is in the next load to carry it over
      this.reputationService.brandSourcesFull$.pipe(filter((sourceMap) => !!sourceMap)),
      this.selectedSourceIds$,
      this.sourceIdCounts$,
      this.translate.stream('COMMON.REVIEW_OR_LISTING_SOURCES'),
    ]).pipe(
      map(([brandSources, selectedSourceIds, sourceIdCounts, sourcesTranslated]) => {
        const sources = selectedSourceIds.map((sourceId) => brandSources.get(sourceId));
        const sourcesInside = sources.filter((source) => source);
        // If not all are present, set to just present and exit early
        if (sources.length - sourcesInside.length) {
          this.setSourceIds(sourcesInside.map((source) => source.sourceId + ''));
          return '';
        }

        // If there's more than X, just display the count of selected
        if (sourcesInside.length > 2) {
          return `${sourcesInside.length} ${sourcesTranslated}`;
        } else {
          // Use counts if present
          if (sourceIdCounts) {
            return sourcesInside
              .map((source) => `${source.name} ${sourceIdCounts.get(source.sourceId + '')}`)
              .join(', ');
          } else {
            return sourcesInside.map((source) => `${source.name}`).join(', ');
          }
        }
      }),
    );

    this.sourceOptions$ = combineLatest([this.sourceIdCounts$$, this.reputationService.brandSourcesFull$]).pipe(
      map(([countMap, sourceMap]) => {
        if (!sourceMap) {
          return [];
        }
        return (
          Array.from(sourceMap.keys())
            .map((sourceId) => {
              const source = sourceMap.get(sourceId + '');
              const count = countMap ? countMap.get(sourceId + '') : null;
              return {
                value: sourceId,
                valueCount: count,
                name: count !== null ? `${source.name} (${count})` : `${source.name}`,
              };
            })
            // TODO: Sort by count?
            .sort((a, b) => a.name.localeCompare(b.name))
        );
      }),
    );

    this.reputationService.brandSources$
      .pipe(takeUntilDestroyed())
      .subscribe((sourceIds) => this.filterService.setEnabledSources(sourceIds.map((sourceId) => sourceId.toString())));
    this.selectedSourceIds$
      .pipe(takeUntilDestroyed())
      .subscribe((sourceIds) => this.filterService.setFilteredSources(sourceIds));

    this.shouldHaveShadow$ = this.router.events.pipe(
      filter((event) => event instanceof ActivationEnd && event.snapshot.children.length === 0),
      map((event: ActivationEnd) => {
        return event.snapshot.data['pageId'] !== PageId.multi_location_report;
      }),
      startWith(
        (function (_this): boolean {
          let route = _this.router.routerState.root;
          while (route.firstChild) {
            route = route.firstChild;
          }
          if (route.snapshot) {
            return route.snapshot.data['pageId'] !== PageId.multi_location_report;
          }
        })(this),
      ),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  setMinimizedStatus(status: boolean): void {
    this.minimised$$.next(status);
  }

  openDialog(dialog: DialogOpenTypes): void {
    this.openDialog$.next(dialog);
  }

  getAccountGroupIds(): string[] {
    return this.selectedAccountGroupIds$$.getValue();
  }
  setAccountGroupIds(accountGroupIds: string[]): void {
    this.selectedAccountGroupIds$$.next(accountGroupIds);
  }
  clearAccountGroupIds(): void {
    this.selectedAccountGroupIds$$.next([]);
  }

  getSourceIds(): string[] {
    return this.selectedSourceIds$$.getValue();
  }
  setSourceIds(sourceIds: string[]): void {
    this.selectedSourceIds$$.next(sourceIds);
  }
  addSourceId(sourceId: string): void {
    const sources = this.selectedSourceIds$$.getValue();
    if (sources.includes(sourceId)) {
      return;
    }
    this.selectedSourceIds$$.next([...sources, sourceId]);
  }
  removeSourceId(sourceId: string): void {
    const sources = this.selectedSourceIds$$.getValue();
    const newSources = sources.filter((id) => id !== sourceId);
    if (sources.length !== newSources.length) {
      this.selectedSourceIds$$.next(newSources);
    }
  }
  clearSourceIds(): void {
    this.selectedSourceIds$$.next([]);
  }
}
